import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import MangaCard from "@/pages/home/components/MangaCard";
import { getMangaBySlug, getMangaByCategory } from "@/mocks/manga";
import { categorySlugMap } from "@/mocks/categories";

export default function MangaPage() {
  const { slug } = useParams<{ slug: string }>();
  const manga = slug ? getMangaBySlug(slug) : undefined;

  if (!manga) {
    return (
      <div className="min-h-screen bg-background-50 font-body">
        <Navbar />
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
          <h1 className="text-xl font-heading font-semibold text-foreground-900 mb-3">漫画未找到</h1>
          <Link to="/" className="text-sm text-primary-600 hover:text-primary-700">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedManga = getMangaByCategory(manga.category).filter((m) => m.id !== manga.id).slice(0, 4);
  const categoryName = categorySlugMap[manga.category] || manga.category;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${manga.title} - 漫画全集在线免费阅读 | 迷妹漫画`,
    description: manga.description,
    author: { "@type": "Person", name: manga.author },
    datePublished: manga.chapters[0]?.date || "2026-01-01",
    dateModified: manga.updatedAt,
    publisher: { "@type": "Organization", name: "迷妹漫画", url: "https://mimeimanga.com/" },
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: `${manga.title}是完结漫画吗？`,
        acceptedAnswer: {
          "@type": "Answer",
          text: manga.status === "已完结"
            ? `是的，${manga.title}是已完结漫画完整版，全部${manga.chapters.length}话均可在迷妹漫画网免费阅读。`
            : `${manga.title}目前仍在连载中，已更新至第${manga.chapters.length}话，最新章节会在迷妹漫画网第一时间上线。`,
        },
      },
      {
        "@type": "Question",
        name: `${manga.title}可以在手机上阅读吗？`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `当然可以！迷妹漫画网支持全设备适配，您可以在手机、平板或电脑上通过下拉式阅读模式畅读${manga.title}的全部章节。`,
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      <div className="min-h-screen bg-background-50 font-body">
        <Navbar />
        <Breadcrumb items={[
          { label: "首页", href: "/" },
          { label: `${categoryName}漫画`, href: `/category/${manga.category}` },
          { label: manga.title },
        ]} />

        <main>
          {/* Manga header */}
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="w-full md:w-[240px] shrink-0">
                <div className="aspect-[5/7] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={manga.coverUrl}
                    alt={manga.title}
                    title={`${manga.title} - 漫画全集在线免费阅读 | 迷妹漫画`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="flex-1">
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">{manga.title}</h1>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-xs text-foreground-500">作者：<strong>{manga.author}</strong></span>
                  <span className="text-xs text-foreground-400">|</span>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${manga.status === "已完结" ? "bg-accent-100 text-accent-700" : "bg-primary-100 text-primary-700"}`}>
                    {manga.status}
                  </span>
                  <span className="text-xs text-foreground-400">|</span>
                  <Link to={`/category/${manga.category}`} className="text-xs text-primary-600 hover:text-primary-700">
                    {categoryName}
                  </Link>
                  <span className="text-xs text-foreground-400">|</span>
                  <span className="flex items-center gap-0.5 text-xs text-foreground-500">
                    <i className="ri-star-fill text-accent-500"></i> {manga.rating}
                  </span>
                  <span className="text-xs text-foreground-400">|</span>
                  <span className="text-xs text-foreground-500">共{manga.chapters.length}话</span>
                  <span className="text-xs text-foreground-400">|</span>
                  <span className="text-xs text-foreground-500">{(manga.views / 10000).toFixed(0)}万次阅读</span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {manga.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[11px] whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-5">{manga.description}</p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
                    从第一话开始阅读
                  </button>
                  <button className="px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap border border-background-200/70">
                    <i className="ri-bookmark-line mr-1.5"></i>加入书架
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Chapter list */}
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-8 border-t border-background-200/70">
            <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-5">章节列表</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {manga.chapters.map((ch) => (
                <div
                  key={ch.number}
                  className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-background-100 hover:bg-primary-50 cursor-pointer transition-colors border border-background-200/70"
                >
                  <span className="text-xs text-foreground-700 truncate">{ch.title}</span>
                  <span className="text-[10px] text-foreground-400 ml-2 whitespace-nowrap">{ch.date}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Related manga */}
          {relatedManga.length > 0 && (
            <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-8 border-t border-background-200/70">
              <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-5">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                {relatedManga.map((m) => (
                  <MangaCard
                    key={m.id}
                    slug={m.slug}
                    title={m.title}
                    coverUrl={m.coverUrl}
                    status={m.status}
                    rating={m.rating}
                    category={categorySlugMap[m.category] || m.category}
                    views={m.views}
                    chapters={m.chapters.length}
                  />
                ))}
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}